import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

import { ITokenProvider } from './authenticationService';
import { IOrderPage, IOrder, IOrderRequest } from 'models/Order';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.locale('sv');

export interface IOrderService {
  getOrders: (
    customerId: string,
    page: number,
    pageSize: number,
    searchText?: string,
    orderTypes?: Number[],
    orderStatuses?: Number[]
  ) => Promise<IOrderPage>;
  saveOrder: (order: IOrderRequest, attachedFiles: File[]) => Promise<IOrder>;
  getOrder: (orderId: string) => Promise<IOrder>;
  getAttachmentContent: (orderId: number, attachmentId: number) => Promise<Blob>;
}

export const orderServiceFactory: (
  ApiHostBaseUrl: string,
  authenticationService: ITokenProvider
) => IOrderService = (ApiHostBaseUrl: string, authenticationService: ITokenProvider) => {
  return {
    getOrder: async (orderId: string) => {
      const accessToken = await authenticationService.getOrRenewNativeToken();
      var url: string = `${ApiHostBaseUrl}/order/${orderId}`;
      const data = await fetch(url, {
        method: 'GET',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          Authorization: 'bearer ' + accessToken,
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      });
      return await (data.json() as Promise<IOrder>);
    },

    getOrders: async (
      customerId: string,
      page: number,
      pageSize: number,
      searchText?: string,
      orderTypes?: Number[],
      orderStatuses?: Number[]
    ) => {
      const orderTypesParam = orderTypes?.map((value) => `&OrderType=${value}`)?.join('') ?? '';
      const orderStatusesParam =
        orderStatuses?.map((value) => `&OrderStatus=${value}`)?.join('') ?? '';

      const accessToken = await authenticationService.getOrRenewNativeToken();
      const url: string = `${ApiHostBaseUrl}/order/customers?customerId=${customerId}&PageNumber=${page}&PageSize=${pageSize}&SearchText=${searchText ?? ''}${orderTypesParam}${orderStatusesParam}`;
      const data = await fetch(url, {
        method: 'GET',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'bearer ' + accessToken,
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      });
      return await (data.json() as Promise<IOrderPage>);
    },

    saveOrder: async (order: IOrderRequest, attachedFiles: File[]) => {
      const accessToken = await authenticationService.getOrRenewNativeToken();
      const url: string = `${ApiHostBaseUrl}/order`;
      const formData = new FormData();
      formData.append('createOrderParameters', JSON.stringify(order));
      attachedFiles &&
        attachedFiles.forEach(function (file) {
          formData.append('files', file);
        });
      const data = await fetch(url, {
        body: formData,
        method: 'POST',
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
        credentials: 'same-origin', // include, *same-origin, omit
        headers: {
          Authorization: 'bearer ' + accessToken,
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
      });
      if (data.status == 200) {
        return data.json() as Promise<IOrder>;
      } else {
        throw new Error('Could not save order');
      }
    },

    getAttachmentContent: async (orderId: number, attachmentId: number) => {
      const url: string = `${ApiHostBaseUrl}/order/${orderId}/download/${attachmentId}`;
      try {
        const accessToken = await authenticationService.getOrRenewNativeToken();
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            Authorization: 'bearer ' + accessToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        });
        return await (response.blob() as Promise<Blob>);
      } catch (error) {
        console.warn('Could not fetch user details');
        throw error;
      }
    },
  };
};
